import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'

import Link from 'next/link'

import { MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getStockStatus } from '@/lib/format'
import { useSettings } from '@/components/generals/settings-provider'
import { MOVEMENT_ICONS } from '@/components/inventory/movement-type-badge'
import { toCatalogProduct, useInventoryActions } from '@/components/inventory/inventory-actions'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import ProductActionsMenu from '@/components/inventory/product-actions-menu'
import StockStatusBadge from '@/components/inventory/stock-status-badge'
import CategoryBadge from '@/components/inventory/category-badge'
import StockMeter from '@/components/inventory/stock-meter'
import UnitLabel from '@/components/inventory/unit-label'

interface ProductListProps {
    products: Product[]
}

interface ProductRowProps {
    product: Product
}

const EntryIcon = MOVEMENT_ICONS.entrada
const ExitIcon = MOVEMENT_ICONS.salida

const QuickMovementButtons: FC<ProductRowProps> = ({ product }): ReactNode => {
    const { openMovement } = useInventoryActions()
    const outOfStock = product.quantity <= 0

    return (
        <div className='flex items-center gap-0.5'>
            <Tooltip>
                <TooltipTrigger
                    render={
                        <Button
                            variant='ghost'
                            size='icon-sm'
                            aria-label={`Registrar entrada de ${product.name}`}
                            onClick={() => openMovement({ product: toCatalogProduct(product), type: 'entrada' })}
                            className='text-flow-in-ink hover:bg-flow-in-soft hover:text-flow-in-ink'
                        />
                    }
                >
                    <EntryIcon aria-hidden='true' />
                </TooltipTrigger>

                <TooltipContent>
                    Registrar entrada
                </TooltipContent>
            </Tooltip>

            <Tooltip>
                <TooltipTrigger
                    render={
                        <Button
                            variant='ghost'
                            size='icon-sm'
                            aria-label={`Registrar salida de ${product.name}`}
                            disabled={outOfStock}
                            onClick={() => openMovement({ product: toCatalogProduct(product), type: 'salida' })}
                            className='text-flow-out-ink hover:bg-flow-out-soft hover:text-flow-out-ink'
                        />
                    }
                >
                    <ExitIcon aria-hidden='true' />
                </TooltipTrigger>

                <TooltipContent>
                    {outOfStock ? 'Sin stock para registrar salidas' : 'Registrar salida'}
                </TooltipContent>
            </Tooltip>
        </div>
    )
}

const ProductTableRow: FC<ProductRowProps> = ({ product }): ReactNode => {
    const { money, number } = useSettings()
    const status = getStockStatus(product.quantity, product.minStock)

    return (
        <tr className='group border-b last:border-b-0 hover:bg-muted/40'>
            <td className='py-3 pr-3 pl-4 sm:pl-5'>
                <Link
                    href={`/inventario/${product.id}`}
                    className='block truncate text-sm font-medium text-foreground hover:text-primary hover:underline underline-offset-4'
                >
                    <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                </Link>

                <p className='mt-0.5 truncate font-mono text-xs text-muted-foreground'>
                    {product.sku ? <span data-mdlfy-cms={`products:${product.id}:sku`}>{product.sku}</span> : 'Sin código'}
                </p>
            </td>

            <td className='hidden px-3 py-3 xl:table-cell'>
                <CategoryBadge id={product.categoryId} name={product.categoryName} color={product.categoryColor} />
            </td>

            <td className='hidden px-3 py-3 text-sm text-muted-foreground 2xl:table-cell'>
                {product.location ? (
                    <span className='line-clamp-1' data-mdlfy-cms={`products:${product.id}:location`}>{product.location}</span>
                ) : (
                    <span className='text-muted-foreground/60'>Sin ubicación</span>
                )}
            </td>

            <td className='px-3 py-3'>
                <div className='flex w-28 flex-col gap-1.5'>
                    <p className='text-sm font-semibold tabular-nums'>
                        <span>{number(product.quantity)}</span> <UnitLabel productId={product.id} unit={product.unit} className='font-normal text-muted-foreground' />
                    </p>

                    <StockMeter quantity={product.quantity} minStock={product.minStock} />

                    <p className='text-[0.7rem] text-muted-foreground tabular-nums'>
                        Mínimo <span>{number(product.minStock)}</span>
                    </p>
                </div>
            </td>

            <td className='px-3 py-3'>
                <StockStatusBadge status={status} />
            </td>

            <td className='px-3 py-3 text-right'>
                <p className='text-sm font-medium tabular-nums'>
                    {money(product.quantity * product.cost)}
                </p>

                <p className='mt-0.5 text-xs text-muted-foreground tabular-nums'>
                    <span>{money(product.cost)}</span> / <UnitLabel productId={product.id} unit={product.unit} />
                </p>
            </td>

            <td className='py-3 pr-3 pl-2 sm:pr-4'>
                <div className='flex items-center justify-end gap-0.5'>
                    <QuickMovementButtons product={product} />
                    <ProductActionsMenu product={product} />
                </div>
            </td>
        </tr>
    )
}

const ProductCard: FC<ProductRowProps> = ({ product }): ReactNode => {
    const { money, number } = useSettings()
    const { openMovement } = useInventoryActions()
    const status = getStockStatus(product.quantity, product.minStock)

    return (
        <li className='rounded-xl border bg-card p-4 shadow-xs'>
            <div className='flex items-start justify-between gap-3'>
                <div className='min-w-0'>
                    <Link
                        href={`/inventario/${product.id}`}
                        className='line-clamp-2 text-[0.95rem] font-semibold text-foreground hover:text-primary'
                    >
                        <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                    </Link>

                    {product.sku && (
                        <p className='mt-0.5 font-mono text-xs text-muted-foreground'>
                            <span data-mdlfy-cms={`products:${product.id}:sku`}>{product.sku}</span>
                        </p>
                    )}
                </div>

                <StockStatusBadge status={status} />
            </div>

            <div className='mt-4 grid grid-cols-2 gap-4'>
                <div className='flex flex-col gap-1.5'>
                    <p className='text-xs text-muted-foreground'>
                        Stock
                    </p>

                    <p className='font-heading text-lg leading-none font-semibold tabular-nums'>
                        <span>{number(product.quantity)}</span> <UnitLabel productId={product.id} unit={product.unit} className='text-sm font-normal text-muted-foreground' />
                    </p>

                    <StockMeter quantity={product.quantity} minStock={product.minStock} />

                    <p className='text-[0.7rem] text-muted-foreground tabular-nums'>
                        Mínimo <span>{number(product.minStock)}</span>
                    </p>
                </div>

                <div className='flex flex-col gap-1.5 text-right'>
                    <p className='text-xs text-muted-foreground'>
                        Valor en stock
                    </p>

                    <p className='font-heading text-lg leading-none font-semibold tabular-nums'>
                        {money(product.quantity * product.cost)}
                    </p>

                    <p className='text-[0.7rem] text-muted-foreground tabular-nums'>
                        <span>{money(product.cost)}</span> / <UnitLabel productId={product.id} unit={product.unit} />
                    </p>
                </div>
            </div>

            <div className='mt-4 flex flex-wrap items-center gap-2'>
                <CategoryBadge id={product.categoryId} name={product.categoryName} color={product.categoryColor} />

                {product.location && (
                    <span className='inline-flex min-w-0 items-center gap-1 text-xs text-muted-foreground'>
                        <MapPin className='size-3.5 shrink-0' aria-hidden='true' />
                        <span className='truncate' data-mdlfy-cms={`products:${product.id}:location`}>{product.location}</span>
                    </span>
                )}
            </div>

            <div className='mt-4 flex items-center gap-2 border-t pt-3'>
                <Button
                    variant='outline'
                    className='flex-1'
                    onClick={() => openMovement({ product: toCatalogProduct(product), type: 'entrada' })}
                >
                    <EntryIcon className='text-flow-in-ink' aria-hidden='true' />
                    Entrada
                </Button>

                <Button
                    variant='outline'
                    className='flex-1'
                    disabled={product.quantity <= 0}
                    onClick={() => openMovement({ product: toCatalogProduct(product), type: 'salida' })}
                >
                    <ExitIcon className='text-flow-out-ink' aria-hidden='true' />
                    Salida
                </Button>

                <ProductActionsMenu product={product} size='icon' />
            </div>
        </li>
    )
}

const ProductList: FC<ProductListProps> = ({ products }): ReactNode => {
    return (
        <>
            <div className='hidden overflow-hidden rounded-xl border bg-card shadow-xs md:block'>
                <table className='w-full table-fixed border-collapse text-left'>
                    <caption className='sr-only'>
                        Productos del inventario con su stock, estado y valor
                    </caption>

                    <thead className='border-b bg-muted/50 text-xs font-medium text-muted-foreground'>
                        <tr>
                            <th scope='col' className='py-2.5 pr-3 pl-4 font-medium sm:pl-5'>Producto</th>
                            <th scope='col' className='hidden w-40 px-3 py-2.5 font-medium xl:table-cell'>Categoría</th>
                            <th scope='col' className='hidden w-40 px-3 py-2.5 font-medium 2xl:table-cell'>Ubicación</th>
                            <th scope='col' className='w-36 px-3 py-2.5 font-medium'>Stock</th>
                            <th scope='col' className='w-30 px-3 py-2.5 font-medium'>Estado</th>
                            <th scope='col' className='w-32 px-3 py-2.5 text-right font-medium'>Valor</th>
                            <th scope='col' className='w-32 py-2.5 pr-4 pl-2 text-right font-medium'>
                                <span className='sr-only'>Acciones</span>
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {products.map(product => (
                            <ProductTableRow key={product.id} product={product} />
                        ))}
                    </tbody>
                </table>
            </div>

            <ul className='flex flex-col gap-3 md:hidden'>
                {products.map(product => (
                    <ProductCard key={product.id} product={product} />
                ))}
            </ul>
        </>
    )
}

export default ProductList
